import { useQuery } from "@tanstack/react-query";
import { supabase } from "../utils/supabaseClient";
import { queryKeys } from "../utils/queryKeys";
import { useUser } from "../contextapi/UserContext";

export type UserRole = "initial" | "user" | "disapproved" | "admin";

interface UserRoleData {
  role: UserRole | null;
  isLoading: boolean;
  error: string | null;
  isInitial: boolean;
  isUser: boolean;
  isDisapproved: boolean;
  isAdmin: boolean;
  canAccessApp: boolean;
  needsApproval: boolean;
}

const isSignupBlockBypassed = () =>
  import.meta.env.VITE_BYPASS_SIGNUP_BLOCK === "true";

export const useUserRole = (): UserRoleData => {
  const { user } = useUser();

  const { data: role, isLoading, error } = useQuery({
    queryKey: queryKeys.user.role(user?.id ?? ''),
    queryFn: async () => {
      const { data: userRole, error: roleError } = await supabase
        .rpc("get_user_role", { user_id: user!.id });

      if (roleError) throw roleError;

      return (userRole ?? "initial") as UserRole;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const resolvedRole = role ?? null;
  const isInitial = resolvedRole === "initial";
  const isUser = resolvedRole === "user";
  const isDisapproved = resolvedRole === "disapproved";
  const isAdmin = resolvedRole === "admin";
  const canAccessApp = isUser || isAdmin || (isInitial && isSignupBlockBypassed());
  const needsApproval = isInitial && !isSignupBlockBypassed();

  return {
    role: resolvedRole,
    isLoading,
    error: error instanceof Error ? error.message : null,
    isInitial,
    isUser,
    isDisapproved,
    isAdmin,
    canAccessApp,
    needsApproval,
  };
};
